<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { get } from 'svelte/store';
    import { _ } from 'svelte-i18n';
    import { onMount } from 'svelte';
    import { Tooltip, tooltip } from '@svelte-plugins/tooltips';

    import TemplateExpandedSectionButtons from '../TemplateExpandedSectionButtons.svelte';
    import TemplateExpandedSection from '../TemplateExpandedSection.svelte';
    import { MapPointLocation } from '../../../utilities/MapPointLocation';
    import MessageDanger from '../../../shared/Messages/MessageDanger.svelte';
    import IconLocationOn from '../../../shared/Icons/IconLocationOn.svelte';
    import ButtonSmallBlack from '../../../shared/ButtonSmallBlack.svelte';
    import { onMapReady } from '../../../utilities/onMapReady';
    import Spinner from '../../../shared/Spinner/Spinner.svelte';
    import type { LocationResponseType } from '../types';
    import { api, clientSettings } from '../../../../stores/api';
    import { LocationApi } from '../LocationApi';
    import InputText from '../../../shared/InputText.svelte';
    import FormRow from '../FormRow.svelte';
    import Label from '../Label.svelte';
    import FormSection from '../FormSection.svelte';
    import ErrorInline from '../../../shared/ErrorInline.svelte';
    import SuccessInline from '../../../shared/SuccessInline.svelte';
    import type { ErrorResponseType } from '@automapa/svelte-automapa-template/dist/Api';
    import Checkbox from '../../../shared/Checkbox/Checkbox.svelte';
    import { debug, jobs } from '../../../../stores/store';
    import ErrorColor from '../../../shared/ErrorColor.svelte';
    import { JobsOnMap } from '../JobsOnMap.js';

    import './JobOne2.css';
    import JobTypeIcon from '../JobTypeIcon.svelte';
    import { captureMessage } from '../../../utilities/Sentry';
    import Switch from '../../../shared/Switch.svelte';
    import JobHistory from '../JobHistory/JobHistory.svelte';
    import { features } from '../../../shared/Features/features';
    import { Location } from '../Location';
    import { getJobColor } from '../../../utilities/getJobColor';
    import Select from '../../../shared/Select.svelte';
    import { NotificationSystem } from '../../NotificationSystem/NotificationSystem';
    import { JobValidator } from '../JobValidator';
    import { app } from '../../../../stores/app';
    import { GeocodeSimpleApi } from '../../../utilities/GeocodeSimpleApi';
    import IconWarning from '../../../shared/Icons/IconWarning.svelte';
    import IconError from '../../../shared/Icons/IconError.svelte';
    import { canWrite } from '../../../shared/Permissions/writeAccess';

    export let job: LocationResponseType;
    export let showHeader: boolean = false;
    export let switchable: boolean = true;
    export let closeButton: boolean = false;
    export let simpleView: boolean = false;
    export let index: number;
    export let expandedIndex: number = -1;
    export let jobsOnMap: JobsOnMap;
    export let fullEditableForm: boolean = false;
    export let enableMap: boolean = true;
    export let enableCheckbox: boolean = true;
    export let locationTypesItems: { id: string; label: string }[] = [];

    let error = '';
    let dispatch = createEventDispatcher();
    let geocodingInProgress: boolean | null = null;
    let geocodingWithSuccess: boolean | null = null;
    let checked: boolean = false;

    let location: Location = new Location().set(job);
    let validateError: string[] = [];

    let isExtIdValid: boolean = true;
    let isFullNameValid: boolean = true;

    function toggle() {
        if (switchable) {
            // https://er.automapa.pl/issues/41683
            expandedIndex = expandedIndex === index ? -1 : index;
            if (expandedIndex !== index) {
                console.log('toggle, closing job ', expandedIndex, index);
                jobsOnMap && jobsOnMap.jobShowBalloon(job.id, false);
                closePointOnMap();
                dispatch('close');
            } else {
                console.log('toggle, opening job ', expandedIndex, index);
                if (job.geom && job.geom.coordinates) {
                    jobsOnMap && jobsOnMap.zoomTo(job.geom.coordinates[0], job.geom.coordinates[1]);
                    jobsOnMap && jobsOnMap.jobShowBalloon(job.id);
                }
                dispatch('open');
            }
        }
    }

    async function validate(): Promise<boolean> {
        validateError = [];

        let l = new Location().set(job);
        let res1 = await JobValidator.checkUniqueExtId($api, l);
        if (res1 !== '') {
            validateError.push(res1);
            isExtIdValid = false;
        } else {
            isExtIdValid = true;
        }

        let res2 = await JobValidator.checkUniqueFullName($api, l, $app.settingsReadonly?.ext_id_must_exists_in_full_name === true);
        if (res2 !== '') {
            validateError.push(res2);
            isFullNameValid = false;
        } else {
            isFullNameValid = true;
        }

        validateError = validateError;
        return isExtIdValid && isFullNameValid;
    }

    async function onUpdate() {
        // Jedyne wąskie gardło każdego zapisu punktu (PUT /location/{id}) - ostatnia linia obrony
        // w GUI, na wypadek gdyby któraś kontrolka nie została wyłączona.
        if (!get(canWrite)) {
            return;
        }
        let validateResult = await validate();
        if (!validateResult) {
            console.log('validateResult', validateResult);
            return;
        }
        closePointOnMap();
        console.log('jobRequest, before parse', job);
        let checkJob: LocationResponseType = await LocationApi.getOne(get(api), job.id).catch((err: ErrorResponseType) => {
            error = err.translation_id;
        });

        let {
            address_id,
            address_attributes,
            client_attributes,
            special_attributes,
            geom,
            ext_id,
            full_name,
            location_type,
            is_ad_hoc,
            coords_accepted,
            ..._rest
        } = job;
        // let jobRequest = extractFieldsFromB<JobRequestType>(job, JobRequestSchema);
        // JobRequestSchema.parse(jobRequest);
        // console.log('jobRequest', jobRequest);
        // {address_id: null, client_attributes: null, job_type_id: 0, address_attributes, special_attributes, target_date, geom, import_id, collection_id, webuser_id }
        let response = await LocationApi.updateOne(get(api), job.id, {
            address_id,
            client_attributes,
            address_attributes,
            special_attributes,
            geom,
            ext_id,
            full_name,
            location_type,
            is_ad_hoc,
            coords_accepted,
        }).catch((err: ErrorResponseType) => {
            error = err.translation_id;
            validateError.push($_('Failed to save changes'));
            validateError = validateError;
            console.warn('validateError', err);
        });
        if (response) {
            job = { ...response };
            location = location.set(job);
            console.log('JobOneRow, job', job);
            error = '';
            $NotificationSystem.reload();
        }
    }

    let mapPointLocation: MapPointLocation | null;

    let startPointOnMap = function () {
        if (!get(canWrite)) {
            return;
        }
        if (mapPointLocation) {
            if (!mapPointLocation.isCurrent()) {
                if (
                    mapPointLocation.start({
                        message: $_('Click on the map to set job #{count} coordinates', {
                            values: { count: job.id },
                        }),
                    })
                ) {
                    dispatch('pointonmapstart');
                }
            } else {
                mapPointLocation.close(true);
            }
        }
    };

    export const closePointOnMap = function () {
        if (mapPointLocation) {
            if (mapPointLocation.close(true)) {
                dispatch('pointonmapclose');
            }
        }
    };

    const setJobCompleted = function () {
        if (
            confirm(
                $_('Are you sure you want to set job #{count} as completed?', {
                    values: { count: job.id },
                })
            )
        ) {
            LocationApi.setCompleted(get(api), job.id).then(response => {
                job = { ...response };
            });
        }
    };

    function editable(job: LocationResponseType) {
        return new Location().set(job).editable();
    }

    // Brak can_write wyłącza cały formularz, ale nie zastępuje reguły editable() - punkt
    // nieedytowalny z powodu swojego stanu zostaje wyłączony niezależnie od uprawnień.
    $: writeBlocked = !$canWrite;
    $: formDisabled = !editable(job) || writeBlocked;

    onMount(() => {
        Promise.all([onMapReady()]).then(response => {
            // console.log('locationTypesItems', locationTypesItems);
            mapPointLocation = new MapPointLocation();
            mapPointLocation.addEventListener('setCoordinates', function (evt: any) {
                // Tryb wskazywania na mapie mógł wystartować, zanim uprawnienia dojechały do
                // store'u - współrzędne z kliknięcia w mapę nie mogą wtedy nadpisać punktu.
                if (!get(canWrite)) {
                    return;
                }
                job.geom = {
                    type: 'Point',
                    coordinates: [evt.detail.coords.x, evt.detail.coords.y],
                };
                onUpdate().then(() => {
                    dispatch('setcoordinates');
                });
            });
            mapPointLocation.addEventListener('close', function () {
                dispatch('pointonmapclose');
            });
        });

        return () => {
            if (mapPointLocation) {
                mapPointLocation.close(true);
            } else {
                // captureMessage('captureMessage: mapPointLocation is not defined');
            }
        };
    });

    $: checked =
        ($jobs.selectMode === 'filtered' && !$jobs.selectedOrUnselected.includes(job.id)) ||
        ($jobs.selectMode === 'selected' && $jobs.selectedOrUnselected.includes(job.id));
</script>

<div class="order-element-v2 order-element-v2-data" class:simple-view={simpleView} class:checked>
    {#if !fullEditableForm}
        <!--        <div class="checkbox">-->
        <!--            {#if enableCheckbox}-->
        <!--                <Checkbox-->
        <!--                    disabled={false}-->
        <!--                    label=""-->
        <!--                    {checked}-->
        <!--                    onChange={() => {-->
        <!--                        if ($jobs.selectedOrUnselected.includes(job.id)) {-->
        <!--                            $jobs.selectedOrUnselected = $jobs.selectedOrUnselected.filter(-->
        <!--                                id => id !== job.id-->
        <!--                            );-->
        <!--                        } else {-->
        <!--                            $jobs.selectedOrUnselected = [...$jobs.selectedOrUnselected, job.id];-->
        <!--                        }-->
        <!--                    }}-->
        <!--                />-->
        <!--            {/if}-->
        <!--        </div>-->

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="location_type light" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
            <span title={location.getLocationType()}>
                <JobTypeIcon type={'delivery'} scale={0.6} color={getJobColor(location)} />
            </span>
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="ext_id light" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
            <div class="three-dots">
                {location.getExtId() || ''}
            </div>
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="full_name bold" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
            <div class="three-dots">
                {location.getFullName() || ''}
            </div>
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div class="pcode light" class:switchable class:extended-background={expandedIndex === index} on:click={toggle}>
            {location.getPostalCode() || ''}
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="city"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
            style="flex-direction: row;justify-content: flex-start; align-items: center;"
        >
            <div class="three-dots dont-break bold">
                {location.getCity() || ''}
            </div>
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="street"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
            style="flex-direction: row;justify-content: flex-start; align-items: center;"
        >
            <div class="street three-dots light dont-break">
                {location.getFullStreet() || ''}
            </div>
        </div>

        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
            class="uwagi"
            class:switchable
            class:extended-background={expandedIndex === index}
            on:click={toggle}
            style="flex-direction: row;justify-content: flex-start; align-items: center;"
        >
            <div style="display: flex; flex-direction: row; column-gap: 6px; align-items: center;">
                {#if !job.coords_accepted || $debug}
                    <span use:tooltip={{ content: $_('low_quality_address'), position: 'left' }}>
                        <IconError scale={0.7} />
                    </span>
                {/if}
            </div>
        </div>
    {/if}

    {#if expandedIndex === index}
        <div class="expanded">
            {#if error}
                <ErrorInline text={$_(error)} />
            {/if}

            <div style="display: flex; justify-content: center; font-size: 14px; padding: 2px;">
                {#if geocodingInProgress !== null}
                    {#if geocodingInProgress}
                        <Spinner />
                    {:else if job.coords_accepted}
                        <SuccessInline text={$_('Geocode success')} />
                    {:else}
                        <ErrorInline text={$_('Geocode failed')} />
                    {/if}
                {/if}
            </div>

            {#if job.address_attributes !== null}
                <FormSection>
                    {#if true}
                        <FormRow>
                            <Label>
                                {$app.settingsReadonly?.short_name_instead_of_unique_id ? $_('ext_id') : $_('Unique id')}
                            </Label>
                            <InputText
                                valid={isExtIdValid}
                                disabled={formDisabled}
                                style="width: 310px;"
                                value={(job?.ext_id && job?.ext_id) || ''}
                                on:change={e => {
                                    let newValue = e.target?.value || '';
                                    job.ext_id = newValue;
                                    onUpdate();
                                }}
                            />
                        </FormRow>
                    {/if}
                    <FormRow>
                        <Label>{$_('full_name')}</Label>
                        <InputText
                            disabled={formDisabled}
                            valid={isFullNameValid}
                            style="width: 310px;"
                            value={(job?.full_name && job?.full_name) || ''}
                            on:change={e => {
                                let newValue = e.target?.value || '';
                                job.full_name = newValue;
                                onUpdate();
                            }}
                        />
                    </FormRow>
                    {#if validateError.length > 0}
                        {#each validateError as error}
                            <ErrorInline text={$_(error)} />
                        {/each}
                    {/if}
                    <FormRow>
                        <Label>{$_('location_type')}</Label>
                        <Select
                            disabled={formDisabled}
                            selected={new Location().set(job).getLocationType() || ''}
                            items={locationTypesItems}
                            onChange={newValue => {
                                job.location_type = newValue;
                                onUpdate();
                                return true;
                            }}
                        />
                        <!--                    <InputText-->
                        <!--                        disabled={!editable(job)}-->
                        <!--                        style="width: 310px;"-->
                        <!--                        value={new Job().set(job).getLocationType() || ''}-->
                        <!--                        on:change={e => {-->
                        <!--                            let newValue = e.target?.value || '';-->
                        <!--                            job.location_type = newValue;-->
                        <!--                            onUpdate();-->
                        <!--                        }}-->
                        <!--                    />-->
                    </FormRow>
                    <FormRow>
                        <Label>{$_('City')}</Label>
                        <InputText
                            disabled={formDisabled}
                            style="width: 310px;"
                            value={(job?.address_attributes && job?.address_attributes['city']) || ''}
                            on:change={e => {
                                let newValue = e.target?.value || '';
                                if (job.address_attributes === null) job.address_attributes = {};
                                job.address_attributes = {
                                    ...job.address_attributes,
                                    city: newValue,
                                };
                                onUpdate();
                            }}
                        />
                    </FormRow>
                    <FormRow>
                        <Label>{$_('Postal code')}</Label>
                        <InputText
                            disabled={formDisabled}
                            style="width: 310px;"
                            value={(job?.address_attributes && job?.address_attributes['pcode']) || ''}
                            on:change={e => {
                                let newValue = e.target?.value || '';
                                if (job.address_attributes === null) job.address_attributes = {};
                                job.address_attributes = {
                                    ...job.address_attributes,
                                    pcode: newValue,
                                };
                                onUpdate();
                            }}
                        />
                    </FormRow>
                    <FormRow>
                        <Label>{$_('Street')}</Label>
                        <InputText
                            disabled={formDisabled}
                            style="width: 310px;"
                            value={(job?.address_attributes && job?.address_attributes['street']) || ''}
                            on:change={e => {
                                let newValue = e.target?.value || '';
                                if (job.address_attributes === null) job.address_attributes = {};
                                job.address_attributes = {
                                    ...job.address_attributes,
                                    street: newValue,
                                };
                                onUpdate();
                            }}
                        />
                    </FormRow>
                    <FormRow>
                        <Label>{$_('House')}</Label>
                        <InputText
                            disabled={formDisabled}
                            style="width: 310px;"
                            value={(job?.address_attributes && job?.address_attributes['house']) || ''}
                            on:change={e => {
                                let newValue = e.target?.value || '';
                                if (job.address_attributes === null) job.address_attributes = {};
                                job.address_attributes = {
                                    ...job.address_attributes,
                                    house: newValue,
                                };
                                onUpdate();
                            }}
                        />
                    </FormRow>
                </FormSection>
            {/if}

            <TemplateExpandedSectionButtons>
                <div
                    style="display: flex; justify-content: space-between; align-items: center; width: 100%; flex-wrap: wrap; padding: 10px 15px; column-gap: 20px; row-gap: 10px;"
                >
                    <div>
                        {#if $features.feature_1475_job_history}
                            <Switch bind:checked={$jobs.showHistory} label={$_('show_job_history')} />
                        {/if}
                    </div>
                    <div style="display: flex; flex-direction:row;justify-content: space-between; align-items: center;  flex-wrap: wrap;">
                        {#if geocodingInProgress}
                            <!--                            <Spinner />-->
                        {:else if job.geom && job.geom.coordinates}
                            <!--{#if enableMap}-->
                            <!--    <IconLocationOn-->
                            <!--        on:click={() => {-->
                            <!--            console.log('job.geom', job.geom);-->
                            <!--            if (job.geom && job.geom.coordinates) {-->
                            <!--                Targeo.Base.Map.zoomAndCenter(-->
                            <!--                    {-->
                            <!--                        x: job.geom.coordinates[0],-->
                            <!--                        y: job.geom.coordinates[1],-->
                            <!--                    },-->
                            <!--                    21-->
                            <!--                );-->
                            <!--                jobsOnMap.jobShowBalloon(job.id);-->
                            <!--            }-->
                            <!--        }}-->
                            <!--    />-->
                            <!--{/if}-->
                        {:else}
                            <div style="opacity:0.5; display: flex;">
                                <ErrorInline text={$_('No coordinates')} />
                                <ErrorColor>
                                    <IconLocationOn />
                                </ErrorColor>
                            </div>
                        {/if}
                        <ButtonSmallBlack
                            text={$_('Geocode again')}
                            disabled={geocodingInProgress === true || formDisabled}
                            showIcon={false}
                            on:click={() => {
                                (async () => {
                                    geocodingInProgress = true;
                                    job.geom = null;
                                    try {
                                        await onUpdate();
                                        if (true) {
                                            geocodingWithSuccess = null;
                                            let response = await GeocodeSimpleApi.geocode(get(api), {
                                                address: {
                                                    country: job.address_attributes?.country,
                                                    city: job.address_attributes?.city,
                                                    pcode: job.address_attributes?.pcode,
                                                    street: job.address_attributes?.street,
                                                    house: job.address_attributes?.house,
                                                },
                                            }).catch(err => {
                                                error = err.translation_id;
                                                // alert(err.translation_id);
                                                geocodingWithSuccess = false;
                                            });
                                            if (response) {
                                                geocodingWithSuccess = true;
                                                job = {
                                                    ...job,
                                                    geom: {
                                                        type: 'Point',
                                                        coordinates: [response.output.x, response.output.y],
                                                    },
                                                    coords_accepted: true,
                                                };
                                            }
                                        }
                                        let j = new Location().set(job);
                                        if (j.hasCoords()) {
                                            console.log('j hasCoords', j);
                                            jobsOnMap.zoomTo(j.getX(), j.getY());
                                            jobsOnMap.jobShowBalloon(j.getId());
                                            jobsOnMap.jobReloadSingle(j.getId());
                                            await onUpdate();
                                        }
                                        dispatch('setcoordinates');
                                        geocodingInProgress = false;
                                    } catch (e) {
                                        alert(e);
                                    }
                                })();
                            }}
                        />
                        {#if switchable && enableMap}
                            <ButtonSmallBlack
                                text={$_('Set on the map')}
                                disabled={geocodingInProgress === true || formDisabled}
                                showIcon={false}
                                on:click={() => {
                                    console.log('job.geom', job?.geom);
                                    let j = new Location().set(job);
                                    if (j.hasCoords()) {
                                        jobsOnMap.zoomTo(j.getX(), j.getY());
                                        jobsOnMap.jobShowBalloon(j.getId());
                                        jobsOnMap.jobReloadSingle(j.getId());
                                    }
                                    // if (job?.geom && job?.geom?.coordinates) {
                                    //     Targeo.Base.Map.zoomAndCenter(
                                    //         {
                                    //             x: job.geom.coordinates[0],
                                    //             y: job.geom.coordinates[1],
                                    //         },
                                    //         21
                                    //     );
                                    //     jobsOnMap.jobShowBalloon(job.id);
                                    // }
                                    startPointOnMap();
                                }}
                            />
                        {/if}
                    </div>
                </div>
            </TemplateExpandedSectionButtons>
            <!--            </div>-->

            {#if $jobs.showHistory && $features.feature_1475_job_history}
                <TemplateExpandedSection>
                    <JobHistory jobId={job.id} {job} />
                </TemplateExpandedSection>
            {/if}

            {#if !switchable}
                <div style="padding:20px;">
                    {#if error}
                        <MessageDanger {error} />
                    {/if}
                    <TemplateExpandedSectionButtons>
                        <ButtonSmallBlack
                            text={$_('Save')}
                            on:click={() => {
                                onUpdate;
                                if (switchable) {
                                    expandedIndex = -1;
                                } else if (closeButton) {
                                    dispatch('close');
                                }
                            }}
                        />
                        {#if closeButton}
                            <ButtonSmallBlack
                                text={$_('Close')}
                                on:click={() => {
                                    dispatch('close');
                                }}
                            />
                        {/if}
                    </TemplateExpandedSectionButtons>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    :global(.job-list-overlay) .checkbox {
        display: none;
    }
    :global(.job-list-overlay) .order-element-v2-data {
        border-bottom: unset;
    }
    .three-dots {
        flex: 0 1 auto;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
</style>
